import * as React from "react";
import styles from "./styles.module.css";
import { useQuery } from "@tanstack/react-query";
import { getSummary, getColumnPreferences } from "../../../services/case-manager/billings";
import {
  formatDate,
  formatSecondsToTime,
  convertUTCToLocal,
  formatLastFirstName,
  compareByLastName,
  patientNameFields
} from "../../../utils/helper";
import { formatUSADate, formatReportMonth } from "../../../utils/dateFormatters";
import Button from "../../button-v1";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import Spinner from "../../spinner";
import logo from "../../../assets/images/toca_health_pdf_logo.png";
import BillingReportPDF from "./BillingReportPDF";
import { buildReportFilename, DOCTYPES } from "../../../utils/fileNaming";

const BillingPdfView = () => {
  const [queryParams] = useSearchParams();
  const [date, setDate] = React.useState("");
  const [billingType, setBillingType] = React.useState("rpm");
  const [locationId, setLocationId] = React.useState(undefined);
  const [patientStatus, setPatientStatus] = React.useState(undefined);

  React.useEffect(() => {
    if (queryParams) {
      setBillingType(queryParams.get("billingType") || "rpm");

      // Get location_pk and patient_status from query params
      const locationPk = queryParams.get("location_pk");
      const patientStatusParam = queryParams.get("patient_status");

      if (locationPk) {
        setLocationId(locationPk);
      }
      if (patientStatusParam) {
        setPatientStatus(patientStatusParam);
      }

      const rawDate = queryParams.get("date");
      if (rawDate) {
        if (rawDate.includes(' ') || rawDate.includes(':')) {
           try {
             const d = new Date(rawDate);
             if (!isNaN(d.getTime())) {
               const year = d.getFullYear();
               const month = d.getMonth() + 1;
               setDate(`${year}-${month}`);
             } else {
               setDate(rawDate);
             }
           } catch (e) {
             setDate(rawDate);
           }
        } else {
           setDate(rawDate);
        }
      }
    }
  }, [queryParams]);

  const {
    data: billingResponse,
    isLoading: isBillingSummaryLoading,
  } = useQuery(
    ["case-manager-billing-summary", date, billingType, locationId, patientStatus, "no-pagination"],
    () => getSummary(
        date,
        billingType,
        locationId,
        undefined, // download
        undefined, // summary
        patientStatus,
        1,         // page (ignored when pagination is false)
        false      // pagination=false
    ),
    {
      enabled: !!date && !!billingType && !date.includes('Tue') && !date.includes('Mon') && !date.includes('Sun'),
    }
  );

  // SCRUM-732: this bulk report reuses the same per-user column
  // preferences as the Billing Dashboard table (view_name matches
  // COLUMN_PREFERENCES_VIEW_NAME in BillingPage.js) so hiding a column
  // there also hides it here, without needing anything passed through the
  // URL. Only the columns below have a direct match in this report's fixed
  // layout (Patient Name/DOB/CPT codes aren't user-configurable on the
  // dashboard either, so they're always shown here too); Consent Date maps
  // to whichever date field this report actually renders per type - Setup
  // Date for RPM (RPM_setup_date), Consent for CCM (ccm_consent_date).
  const { data: columnPreferences } = useQuery(
    ["billing-column-preferences", "billing_summary"],
    () => getColumnPreferences("billing_summary")
  );
  // Matches DEFAULT_VISIBLE_COLUMNS in BillingPage.js: when nothing's been
  // saved yet, generic columns default visible, program-specific ones
  // default hidden - same default either place a user first sees this.
  const GENERIC_DEFAULT_VISIBLE_KEYS = ["facility_provider", "insurance", "icd_codes_grouped", "dos"];
  const isColumnVisible = key => {
    const saved = columnPreferences?.columns;
    if (!Array.isArray(saved)) return GENERIC_DEFAULT_VISIBLE_KEYS.includes(key);
    return saved.includes(key);
  };
  const visibleColumns = {
    provider: isColumnVisible("facility_provider"),
    icd: isColumnVisible("icd_codes_grouped"),
    consent: isColumnVisible(billingType === "rpm" ? "rpm_setup_date" : "ccm_consent_date"),
    dos: isColumnVisible("dos")
  };

  const billingSummary = billingResponse?.results || billingResponse || {};

  // SCRUM-604: this view fetches the full (unpaginated) patient list, so -
  // unlike the paginated Billing Dashboard table - a client-side sort here
  // is globally correct, not just correct within a page. Sorted by last
  // name (case-insensitive), first name as tiebreaker.
  const patientsList = [...(billingSummary.patients || [])].sort((a, b) =>
    compareByLastName(patientNameFields(a), patientNameFields(b))
  );

  // Reconstruct an object that BillingReportPDF expects (it expects a 'data' prop with a 'patients' array)
  const pdfData = { ...billingSummary, patients: patientsList };

  // Floating horizontal scrollbar (SCRUM-...): the table can be much wider
  // than the viewport, and the page can be much taller than the table (many
  // patients), so the native scrollbar sitting at the table's own bottom
  // edge could be far below the fold. This mirrors it into a thin bar fixed
  // to the window bottom instead — scrolling either one moves the other,
  // via scrollLeft, which browsers don't re-fire a 'scroll' event for when
  // set to the value it already has, so the two handlers don't ping-pong.
  const tableScrollRef = React.useRef(null);
  const floatingScrollRef = React.useRef(null);
  const [scrollTrack, setScrollTrack] = React.useState({ left: 0, width: 0, contentWidth: 0 });

  React.useEffect(() => {
    const measure = () => {
      const el = tableScrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setScrollTrack({ left: rect.left, width: rect.width, contentWidth: el.scrollWidth });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [patientsList.length, billingType]);

  const handleTableScroll = (e) => {
    if (floatingScrollRef.current) floatingScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  const handleFloatingScroll = (e) => {
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  const handleDownloadPDF = async () => {
    const toastId = toast.loading("Generating PDF...");
    try {
      const blob = await pdf(
        <BillingReportPDF
          data={pdfData}
          date={date}
          type={billingType}
          logo={logo}
          formatDate={formatReportMonth}
          formatUSA={formatUSADate}
          formatSecondsToTime={formatSecondsToTime}
          convertUTCToLocal={convertUTCToLocal}
          visibleColumns={visibleColumns}
        />
      ).toBlob();
      
      const [year, month] = date ? date.split("-") : [undefined, undefined];
      const fileName = buildReportFilename({
        year,
        month,
        doctype: DOCTYPES.BILLING,
        program: billingType === "all" ? ["RPM", "CCM"] : billingType,
        ext: "pdf",
      });
      saveAs(blob, fileName);
      toast.success("Download Successful!", { id: toastId });
    } catch (error) {
      console.error("PDF generation error", error);
      toast.error("Failed to generate PDF", { id: toastId });
    }
  };

  if (isBillingSummaryLoading) return <div className={styles.loadingContainer}><Spinner /></div>;

  const renderStatusPill = (billings, code) => {
    // Filter for multiple entries, EXCLUDING non_billable
    const matchingBillings = billings?.filter(b => b.code === code && b.status !== 'non_billable') || [];
    
    if (matchingBillings.length === 0) return <span className={styles.dash}>--</span>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
            {matchingBillings.map((bill, idx) => {
                let statusClass = styles.statusDefault;
                if (bill.status === 'billed') statusClass = styles.statusBilled;
                if (bill.status === 'billable') statusClass = styles.statusBillable;
                
                return (
                    <span key={idx} className={`${styles.statusPill} ${statusClass}`}>
                        {bill.status}
                        {bill.units > 1 && <span className={styles.units}> x{bill.units}</span>}
                    </span>
                );
            })}
        </div>
    );
  };

  const isRPM = billingType === 'rpm';

  return (
    <>
      <section className={styles.download_button}>
        <Button onClick={handleDownloadPDF}>Download PDF</Button>
      </section>

      <main className={styles.container}>
        <section className={styles.header}>
          <img src="/assets/images/th_logo.png" alt="Logo" className={styles.th_logo} />
          <div className={styles.separator}></div>
          <p className={styles.location}>
            {JSON.parse(sessionStorage.getItem("current_loc"))?.name}
          </p>
        </section>

        <section className={styles.patientInfo}>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Month</label>
            <p className={styles.value}>{formatReportMonth(date)}</p>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.label}>Report Type</label>
            <p className={styles.value}>{billingType.toUpperCase()} Billing</p>
          </div>
          
        </section>

        <div className={styles.separator} style={{ margin: '20px 0' }}></div>

        <div className={styles.tableScrollWrapper}>
        <section
          className={styles.tableContainer}
          ref={tableScrollRef}
          onScroll={handleTableScroll}
        >
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Patient Name</th>
                <th>DOB</th>
                {visibleColumns.provider && <th>Provider</th>}
                {visibleColumns.icd && <th>ICD-10</th>}
                {visibleColumns.consent && <th>Consent Date</th>}
                {isRPM && visibleColumns.dos && <th>DOS</th>}
                {isRPM ? (
                    <>
                        <th>99453</th>
                        <th>99445</th>
                        <th>99454</th>
                        <th>99470</th>
                        <th>99457</th>
                        <th>99458</th>
                    </>
                ) : (
                    <>
                        <th>G0506</th>
                        <th>99490</th>
                        <th>99439</th>
                        <th>99487</th>
                        <th>99489</th>
                    </>
                )}
              </tr>
            </thead>
            <tbody>
              {patientsList.map((patient) => {
                  const billings = isRPM ? patient.rpm_billings : patient.ccm_billings;
                  
                  const consentDateRaw = isRPM 
                    ? (patient.RPM_setup_date || patient.created_at) 
                    : patient.created_at;
                  const consentDate = consentDateRaw ? formatUSADate(consentDateRaw) : "--";
                  
                  // DOS Logic for HTML Preview
                  let dosRender = "--";
                  if (isRPM) {
                      const targetCodes = ["99445", "99454"];
                      const relevantBillings = patient.rpm_billings?.filter(b => targetCodes.includes(b.code)) || [];
                      
                      if (relevantBillings.length > 0) {
                          const entries = [];
                          relevantBillings.forEach(bill => {
                              if (bill.dates_of_service) {
                                  bill.dates_of_service.forEach(dateStr => {
                                      const [y, m, d] = dateStr.split('-');
                                      // Push object
                                      entries.push({ date: `${m}-${d}-${y}`, code: bill.code });
                                  });
                              }
                          });
                          
                          if (entries.length > 0) {
                              dosRender = (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
                                      {entries.map((entry, i) => (
                                          <span key={i} style={{ whiteSpace: 'nowrap' }}>
                                            {entry.date} <span style={{ color: '#888888', fontStyle: 'italic', fontSize: '13px' }}>({entry.code})</span>
                                          </span>
                                      ))}
                                  </div>
                              );
                          }
                      } else {
                          // Fallback
                          const oldDate = convertUTCToLocal(patient.code_stats?.[0]?.[99454]?.created_at);
                          if(oldDate) dosRender = oldDate;
                      }
                  }

                  return (
                    <tr key={patient.patient_id} className={styles.tableRow}>
                      <td className={styles.tableCell}>{formatLastFirstName(patientNameFields(patient))}</td>
                      <td className={styles.tableCell}>{formatUSADate(patient.dob)}</td>
                      {visibleColumns.provider && (
                        <td className={styles.tableCell}>{patient.provider || "--"}</td>
                      )}
                      {visibleColumns.icd && (
                        <td className={styles.tableCell}>
                          {isRPM
                            ? (patient.rpm_codes?.join(", ") || "--")
                            : (patient.icd_codes?.join(", ") || "--")}
                        </td>
                      )}
                      {visibleColumns.consent && (
                        <td className={styles.tableCell}>{consentDate}</td>
                      )}

                      {isRPM && visibleColumns.dos && (
                        <td className={styles.tableCell}>{dosRender}</td>
                      )}

                      {isRPM ? (
                        <>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99453")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99445")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99454")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99470")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99457")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99458")}</td>
                        </>
                      ) : (
                        <>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "G0506")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99490")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99439")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99487")}</td>
                          <td className={styles.tableCell}>{renderStatusPill(billings, "99489")}</td>
                        </>
                      )}
                    </tr>
                  );
              })}
            </tbody>
          </table>
        </section>
        </div>
      </main>

      {scrollTrack.contentWidth > scrollTrack.width && (
        <div
          className={styles.floatingScrollTrack}
          ref={floatingScrollRef}
          onScroll={handleFloatingScroll}
          style={{ left: scrollTrack.left, width: scrollTrack.width }}
        >
          <div className={styles.floatingScrollInner} style={{ width: scrollTrack.contentWidth }} />
        </div>
      )}
    </>
  );
};

export default BillingPdfView;